import * as d3 from "d3";
import { useEffect, useRef, useState, useContext } from "react";
import { Modal, Steps, Button, Collapse, Timeline, Tag, Card, Row, Col } from "antd";
import {
  EnvironmentOutlined,
  ClockCircleFilled,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  FileTextOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import SampleImg from "../../assests/sample.jpg";
import ImproperLog from "../ImproperLog";

const { Step } = Steps;
const { Panel } = Collapse;

export default function CircularOverAll({ onBack }) {
  const ref = useRef();
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month());
  const { authData, backUrl } = useContext(AuthContext);
  const [overAllMonthData, setOverAllMonthData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [loading, setLoading] = useState(false);

  const [showOnDuty, setShowOnDuty] = useState(false);
  const [onDutyRequests, setOnDutyRequests] = useState([]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/hr/attendance-comparsion/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
        params: { month: month + 1, year },
      });
      setOverAllMonthData(res.data.results || []);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setOverAllMonthData([]);
    }
  };

  const fetchOnduty = async (date) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backUrl}/api/hr/attendance/my-onduty-requests/?from_date=${date}&to_date=${date}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setOnDutyRequests(response.data);
    } catch (err) {
      console.log(err.response?.data?.message || err.message);
      console.log("Failed to fetch onduty requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, year]);

  const filteredData = overAllMonthData.filter(
    (item) => item.Date === selectedDate
  );

  // Filter to only show records with FirstInTime
  const validFilteredData = filteredData.filter(
    (item) => item?.FirstInTime !== null && item?.FirstInTime !== undefined
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "#16a34a";
      case "PENDING":
        return "#d97706";
      case "REJECTED":
        return "#dc2626";
      default:
        return "#888";
    }
  };

  const getAttendanceStatusColor = (status, remarks, leaveType) => {
    const s = (status || "").toUpperCase();
    const r = (remarks || "").toUpperCase();
    const sec = (leaveType || "").toUpperCase();

    // 0. General Permission Check (Distinct Royal Indigo #4F46E5)
    const isGenPerm =
      s.includes("GENERAL PERMISSION") ||
      s.includes("GENERAL_PERMISSION") ||
      s === "GEN_PERM" ||
      s === "GEN PERM" ||
      r.includes("GENERAL PERMISSION") ||
      r.includes("GENERAL_PERMISSION") ||
      r.includes("SPECIAL PERMISSION") ||
      r.includes("PERMISSION CONSIDERATION") ||
      sec.includes("GENERAL PERMISSION") ||
      sec.includes("GENERAL_PERMISSION") ||
      sec.includes("SPECIAL PERMISSION");

    if (isGenPerm) return "#4F46E5";

    // Clean remarks of "on behalf of" so "behalf" does not match "HALF"
    const rClean = r.replace(/\bON BEHALF OF\b/g, "").replace(/\bBEHALF\b/g, "");

    // 1. Week off / Sunday / Off Day
    if (s.includes("WEEK") || rClean.includes("WEEK") || s.includes("OFF DAY") || rClean.includes("OFF DAY") || s === "WO" || sec.includes("WEEK") || s === "SUNDAY") {
        if (s.includes("HALF") || rClean.includes("HALF")) return "#ffcc00"; // Half day weekoff
        return "#FFFF00";
    }

    // 2. Comp Off
    if (s.includes("COMP") || rClean.includes("COMP") || sec.includes("COMP")) {
        if (s.includes("HALF") || rClean.includes("HALF")) return "#66ffff"; // Half day compoff
        return "#1FA2CC";
    }

    // 3. Shortfall Present (Evaluated before generic Half-Day so Half Day Shortfall gets correct teal color)
    if (s.includes("SHORTFALL") || rClean.includes("SHORTFALL")) {
        if (s.includes("HALF") || rClean.includes("HALF")) return "#58BABB"; // Medium teal #58BABB
        return "#298988"; // Dark teal #298988
    }

    // 4. Half Day Specific Leaves
    if (rClean.includes("HALF DAY - SICK") || (rClean.includes("HALF") && sec.includes("SICK"))) {
        return "#B1A1C8";
    }
    if (rClean.includes("HALF DAY - CASUAL") || (rClean.includes("HALF") && sec.includes("CASUAL"))) {
        return "#FE66CB";
    }
    if (s.includes("HALF") || rClean.includes("HALF")) {
        return "#FE66CB";
    }

    // 7. Permission
    if (s.includes("PERMISSION") || rClean.includes("PERMISSION") || sec.includes("PERMISSION")) {
        return "#98FF66";
    }

    // 8. Holiday
    if (s.includes("HOLIDAY") || rClean.includes("HOLIDAY")) {
        return "#F79647";
    }

    // 9. Late In / Grace / Early Out
    if (s.includes("LATE") || rClean.includes("LATE") || s.includes("GRACE") || rClean.includes("GRACE") || s.includes("EARLY")) {
        return "#FFFECB";
    }

    // 10. Present
    if (s.includes("PRESENT") || rClean.includes("PRESENT")) {
        return "#00AF50";
    }

    // 11. Absent
    if (s.includes("ABSENT") || rClean.includes("ABSENT")) {
        return "#FE0000";
    }

    const statusColorMap = {
      PRESENT: "#00AF50",                        // dark green
      ABSENT: "#FE0000",                         // red
      "GRACE - LATE IN": "#FFFECB",             // light yellow
      "GRACE - EARLY OUT": "#FFFECB",
      "GRACE – EARLY OUT": "#FFFECB",           // light yellow
      "LATE IN": "#FFFECB",                      // light yellow
      "EARLY OUT": "#FFFECB",                    // light yellow
      "SHORTFALL PRESENT": "#298988",            // dark teal
      "HALF DAY - SHORTFALL PRESENT": "#58BABB", // medium teal/cyan
      "SHORT HOURS": "#DED9C5",                  // beige/tan
      "MISPUNCH OUT": "#BFBFBF",                 // medium gray
      "MISPUNCH IN": "#BFBFBF",                  // medium gray
      PERMISSION: "#98FF66",                     // bright lime green
      "GENERAL PERMISSION": "#4F46E5",           // royal indigo
      GENERAL_PERMISSION: "#4F46E5",
      "GEN PERM": "#4F46E5",
      GEN_PERM: "#4F46E5",
      HOLIDAY: "#F79647",                        // orange
      SUNDAY: "#FFFF00",                         // pure yellow
      COMPOFF: "#1FA2CC",                        // sky/cyan blue
      DEFAULT: "white",
      WEEKOFF: "#FFFF00",                        // pure yellow
      "WEEK OFF": "#FFFF00",
      "WEEK_OFF": "#FFFF00",
      WO: "#FFFF00",
      "OFF DAY": "#FFFF00",
      TECHNICAL_ISSUE: "#303343",
      CASUAL: "#CB00CC",                         // magenta/purple
      SICK: "#FF0066",                           // hot pink / deep pink
      "HALF DAY - CASUAL LEAVE": "#FE66CB",     // light pink
      "HALF DAY - SICK LEAVE": "#B1A1C8",
      "HALF DAY - CASUAL": "#FE66CB",
      "HALF DAY - SICK": "#B1A1C8",
      "LOP": "#bf4406",
      "HALF DAY - COMPOFF": "#66ffff",
      "HALF DAY - WEEKOFF": "#ffcc00",
    };

    return statusColorMap[sec] || statusColorMap[r] || statusColorMap[s] || "#00AF50";
  };

  const getDayColor = (dateStr) => {
    const day = dayjs(dateStr).startOf("day");
    const today = dayjs().startOf("day");

    // If it's a future date, return white with no records
    if (day.isAfter(today)) {
      return "#ffffff";
    }

    const dayRecords = overAllMonthData.filter((item) => item.Date === dateStr);
    if (!dayRecords.length) {
      if (day.day() === 0) return "#FFFF00"; // Sunday is always yellow
      return "#d1d5db";
    }
    let color = day.day() === 0 ? "#FFFF00" : "#d1d5db";
    for (const rec of dayRecords) {
      const c = getAttendanceStatusColor(rec.Status, rec.AttendanceStatus, rec.LeaveType);
      if (c && c !== "white" && c !== "#ffffff" && c !== "#d1d5db") {
        color = c;
        break;
      }
    }
    return color;
  };

  useEffect(() => {
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const width = ref.current.clientWidth;
    const isMobile = width < 768;
    const height = isMobile ? 320 : 420;
    const cellSize = isMobile ? 40 : 70;
    const radius = isMobile ? 15 : 25;

    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);
    const startDate = d3.timeSunday.floor(startOfMonth);
    const endDate = d3.timeSaturday.ceil(endOfMonth);
    const days = d3.timeDays(startDate, d3.timeDay.offset(endDate, 1));

    const numWeeks = Math.ceil(days.length / 7);
    const totalWidth = 7 * cellSize;
    const totalHeight = numWeeks * cellSize;

    const offsetX = (width - totalWidth) / 2 + cellSize / 2;
    const offsetY = (height - totalHeight) / 2 + 30;

    const g = svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .append("g")
      .attr("transform", `translate(${offsetX}, ${offsetY})`);

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    g.selectAll("text-weekday")
      .data(daysOfWeek)
      .enter()
      .append("text")
      .attr("x", (d, i) => i * cellSize)
      .attr("y", -30)
      .attr("text-anchor", "middle")
      .attr("font-size", width < 500 ? "11px" : "14px")
      .attr("font-weight", "600")
      .attr("fill", "#0e497b")
      .text((d) => d);

    g.selectAll("circle-day")
      .data(days)
      .enter()
      .append("circle")
      .attr("cx", (d) => d.getDay() * cellSize)
      .attr("cy", (d, i) => Math.floor(i / 7) * cellSize)
      .attr("r", radius)
      .attr("fill", (d) => getDayColor(d3.timeFormat("%Y-%m-%d")(d)))
      .attr("stroke", (d) => {
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        const bgColor = getDayColor(dateStr);
        return bgColor === "#ffffff" ? "#e5e7eb" : "#fff";
      })
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        setSelectedDate(dateStr);
        setIsModalOpen(true);
        fetchOnduty(dateStr);
      })
      .on("mouseover", function () {
        d3.select(this).transition().duration(120).attr("r", radius + 4);
      })
      .on("mouseout", function () {
        d3.select(this).transition().duration(120).attr("r", radius);
      });

    g.selectAll("text-day")
      .data(days)
      .enter()
      .append("text")
      .attr("x", (d) => d.getDay() * cellSize)
      .attr("y", (d, i) => Math.floor(i / 7) * cellSize + 5)
      .attr("text-anchor", "middle")
      .attr("font-size", width < 500 ? "10px" : "12px")
      .attr("font-weight", "bold")
      .attr("fill", (d) => {
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        const bgColor = getDayColor(dateStr);
        if (d.getMonth() !== month) return "#9ca3af";
        // If background is white or light yellow, use dark text.
        if (
          bgColor?.toUpperCase() === "#FFFFFF" ||
          bgColor?.toUpperCase() === "#FFFECB" ||
          bgColor?.toUpperCase() === "#FFFF00"
        )
          return "#333";
        return "#fff";
      })
      .text((d) => d.getDate());
  }, [month, year, overAllMonthData]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const renderOnDutyRequests = () => {
    if (!showOnDuty || !onDutyRequests || onDutyRequests.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: 30, padding: isMobile ? "0 10px" : "0 40px" }}>
        <h3
          style={{
            fontSize: isMobile ? 18 : 20,
            fontWeight: 700,
            marginBottom: 20,
            color: "#0e497b",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FileTextOutlined />
          On-Duty Requests
        </h3>

        {onDutyRequests.map((request) => (
          <Card
            key={request.id}
            style={{
              marginBottom: 20,
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `2px solid ${getStatusColor(request.status)}`,
            }}
            bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#333", marginBottom: 8 }}>
                  {request.employee_name} ({request.employee_code})
                </div>
                <div style={{ marginBottom: 6 }}>
                  <p
                    style={{
                      fontSize: isMobile ? 12 : 13,
                      color:
                        request.status === "APPROVED" ? "success"
                          : request.status === "PENDING" ? "warning"
                            : "error",
                    }}
                  >
                    {request.status}
                  </p>
                </div>
                <p><strong>Onduty Type:</strong> {request.onduty_type.toUpperCase()}</p>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
                marginBottom: 16,
                backgroundColor: "#ffffff",
                padding: isMobile ? "12px" : "16px",
                borderRadius: 8,
                border: "1px solid #ffffff",
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />Start Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.start_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />End Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.end_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />Location
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {request.location}
                </div>
              </div>
              {request.remarks && (
                <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Remarks</div>
                  <div style={{ fontSize: isMobile ? 13 : 14, color: "#374151", fontStyle: "italic" }}>
                    "{request.remarks}"
                  </div>
                </div>
              )}
            </div>

            <Collapse bordered={false} style={{ backgroundColor: "#fff", borderRadius: 8 }}>
              <Panel
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>
                    Approval Logs ({request.approval_logs?.filter((log) => log.status === "APPROVED").length || 0}/{request.approval_logs?.length || 0})
                  </div>
                }
                key="1"
              >
                <Timeline
                  style={{ marginTop: 16 }}
                  items={request.approval_logs?.map((log) => ({
                    color: getStatusColor(log.status),
                    children: (
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, color: "#333" }}>{log.approver_name}</div>
                            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                              Updated: {dayjs(log.updated_at).format("MMM DD, YYYY hh:mm A")}
                            </div>
                          </div>
                          <Tag color={log.status === "APPROVED" ? "success" : log.status === "PENDING" ? "warning" : "error"} style={{ fontSize: 12 }}>
                            {log.status}
                          </Tag>
                        </div>
                        <div style={{ backgroundColor: "#f9fafb", padding: "8px 12px", borderRadius: 6, fontSize: 13, color: "#555", fontStyle: "italic", marginTop: 8 }}>
                          <strong>Remarks: </strong>{log.remarks ? log.remarks : "No remarks found"}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </Panel>
            </Collapse>
          </Card>
        ))}
      </div>
    );
  };

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear((prev) => prev - 1); }
    else setMonth((prev) => prev - 1);
  };

  const goNext = () => {
    if (month === 11) { setMonth(0); setYear((prev) => prev + 1); }
    else setMonth((prev) => prev + 1);
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, marginTop: 10 }}>
        <Button
          onClick={onBack}
          icon={<CalendarOutlined />}
          style={{ backgroundColor: "#0e497b", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600 }}
        >
          Show Today's Attendance
        </Button>
      </div>

      <div
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          width: "100%", minHeight: "90vh", background: "#f8fafc", padding: "20px 10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <Button icon={<LeftOutlined />} onClick={goPrev} />
          <strong style={{ fontSize: "clamp(16px, 2vw, 20px)", margin: "0 10px", color: "#0e497b" }}>
            {monthNames[month]} {year}
          </strong>
          <Button icon={<RightOutlined />} onClick={goNext} />
        </div>

        <svg ref={ref} style={{ width: "95%", maxWidth: 700, height: 420 }}></svg>

        {/* ---------- LEGEND SECTION ---------- */}
        <div
          style={{
            marginTop: 16,
            marginBottom: 16,
            padding: "16px 18px",
            background: "#ffffff",
            borderRadius: 14,
            width: "95%",
            maxWidth: 700,
            textAlign: "left",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0e497b", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 }}>
            🎨 Attendance Status Color Code Legend
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              rowGap: 10,
              columnGap: 14,
              fontSize: 13,
            }}
          >
            {[
              { label: "Present", color: "#00AF50" },
              { label: "Permission", color: "#98FF66" },
              { label: "General Permission", color: "#4F46E5" },
              { label: "Late In / Early Out", color: "#FFFECB" },
              { label: "Shortfall Present", color: "#298988" },
              { label: "HalfDay Shortfall", color: "#58BABB" },
              { label: "Week Off", color: "#FFFF00" },
              { label: "Comp Off", color: "#1FA2CC" },
              { label: "Sick Leave", color: "#FF0066" },
              { label: "Casual Leave", color: "#CB00CC" },
              { label: "Half Day Sick", color: "#B1A1C8" },
              { label: "Half Day Casual", color: "#FE66CB" },
              { label: "Short Hours", color: "#DED9C5" },
              { label: "Mispunch IN / OUT", color: "#BFBFBF" },
              { label: "Absent", color: "#FF0000" },
              { label: "Holiday", color: "#FFA040" },
              { label: "Loss of Pay (LOP)", color: "#bf4406" },
              { label: "Half Day Comp Off", color: "#66ffff" },
              { label: "Half Day Week Off", color: "#ffcc00" },
              { label: "Technical Issue", color: "#474851" }
            ].map((item) => (
              <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    background: item.color,
                    borderRadius: "50%",
                    border: "1px solid rgba(0,0,0,0.2)",
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span style={{ color: "#0f172a", fontWeight: 700, fontSize: 12.5 }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- EXECUTIVE CALENDAR DAY RECORD DETAILS MODAL --- */}
      <Modal
        title={
          <div style={{ paddingBottom: 6, textAlign: "left" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Attendance Log</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#64748b", marginTop: 2 }}>
              {selectedDate ? dayjs(selectedDate).format("dddd, MMMM D, YYYY") : ""}
            </div>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        width={580}
        destroyOnClose
      >
        {validFilteredData.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 20px" }}>
            <CalendarOutlined style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
            <div style={{ fontSize: 16, fontWeight: 800, color: "#334155" }}>No Punches Recorded</div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>There are no biometric or mobile check-in logs for this date.</div>
          </div>
        ) : (
          <div style={{ paddingTop: 8, textAlign: "left" }}>
            {(() => {
              const firstItem = validFilteredData[0];
              const status = firstItem?.Status?.toUpperCase() || firstItem?.AttendanceStatus?.toUpperCase() || "DEFAULT";
              const remarks = firstItem?.AttendanceStatus || firstItem?.StatusMessage || firstItem?.LeaveType || "No Record";
              const statusColor = getAttendanceStatusColor(firstItem.AttendanceStatus || firstItem.Status, firstItem.StatusMessage || firstItem.AttendanceStatus, firstItem.LeaveType);

              return (
                <>
                  {/* Status micro-bar */}
                  <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div className="d-flex align-items-center gap-1.5">
                      <span style={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>Status:</span>
                      <Tag
                        color={status === "PRESENT" ? "success" : status === "ABSENT" ? "error" : "default"}
                        style={{ fontSize: 11.5, fontWeight: 800, padding: "2px 8px", borderRadius: 6, margin: 0 }}
                      >
                        {status || "No Record"}
                      </Tag>
                    </div>

                    <div className="d-flex align-items-center gap-1.5">
                      <span style={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>Remarks:</span>
                      <Tag
                        color={statusColor}
                        style={{ fontSize: 11.5, fontWeight: 800, padding: "2px 8px", borderRadius: 6, margin: 0 }}
                      >
                        {remarks}
                      </Tag>
                    </div>
                  </div>

                  <ImproperLog record={firstItem} />
                </>
              );
            })()}

            {/* ON DUTY REQUESTS SECTION */}
            {renderOnDutyRequests()}
          </div>
        )}
      </Modal>
    </>
  );
}